import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  ExternalLink,
  Send,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import {
  createTeam,
  fetchPremiumSocial,
  inviteTeamMember,
  kickTeamMember,
  leaveTeam,
  requestJoinTeam,
  respondTeamRequest,
  deleteTeam,
  updateTeam,
} from "../../../api/premiumSocialService";
import ProfileSectionCard from "../components/ProfileSectionCard";
import { useProfileHub } from "../ProfileHubContext";

function FriendActionButton({ children, onClick, tone = "dark", icon: Icon, disabled }) {
  const tones = {
    dark: "bg-slate-950 text-white",
    green: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-rose-600 text-white",
    light: "bg-white text-slate-700 border border-slate-200",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold disabled:opacity-50 ${tones[tone] || tones.dark}`}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      {children}
    </button>
  );
}

export default function ProfileTeamSection() {
  const navigate = useNavigate();
  const { profileIdentity } = useProfileHub();
  const [social, setSocial] = useState({
    teams: [],
    public_teams: [],
    team_requests: [],
    suggested_users: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [forms, setForms] = useState({
    teamName: "",
    teamTag: "",
    teamLogo: null,
    teamInviteTeamId: "",
    teamInviteUserId: "",
    teamBio: "",
    teamBanner: null,
    teamEditLogo: null,
  });

  const refreshSocial = async (signal) => {
    const payload = await fetchPremiumSocial(signal);
    setSocial((prev) => ({ ...prev, ...payload }));
    return payload;
  };

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    refreshSocial(controller.signal)
      .catch(() => setError("Chargement équipes impossible."))
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, []);

  const myTeam = useMemo(
    () => (social.teams || [])[0] || null,
    [social.teams]
  );
  const publicTeams = social.public_teams || [];
  const teamRequests = social.team_requests || [];

  const amOwnerOrCo = useMemo(
    () =>
      (myTeam?.members || []).some(
        (m) => m.user.id === profileIdentity.id && (m.role === "chef" || m.role === "co_chef")
      ),
    [myTeam, profileIdentity.id]
  );

  useEffect(() => {
    if (myTeam) {
      setForms((prev) => ({
        ...prev,
        teamBio: myTeam.bio || "",
      }));
    }
  }, [myTeam]);

  const updateForm = (field, value) =>
    setForms((prev) => ({ ...prev, [field]: value }));

  const runAction = async (callback) => {
    setMessage("");
    setError("");
    try {
      const response = await callback();
      setMessage(response.message || "Action effectuée.");
      await refreshSocial();
      return response;
    } catch (actionError) {
      setError(
        actionError?.response?.data?.error ||
          actionError?.message ||
          "Action impossible."
      );
      return null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-[28px] bg-white/60" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {message}
        </div>
      )}
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Mon équipe ou Créer une équipe */}
      {myTeam ? (
        <ProfileSectionCard
          title="Mon équipe"
          subtitle={`[${myTeam.tag}] ${myTeam.name} — ${myTeam.members?.length || 0} / 20 membres`}
        >
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(`/equipe/t/${myTeam.id}`)}
                className="inline-flex items-center gap-2 rounded-full bg-pixy-ink px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Voir la page équipe
              </button>
              <FriendActionButton
                tone="light"
                icon={X}
                onClick={() => runAction(() => leaveTeam(myTeam.id))}
              >
                Quitter l'équipe
              </FriendActionButton>
            </div>

            {/* Gérer l'équipe : bio, avatar, couleur de bannière */}
            {amOwnerOrCo && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 text-sm font-semibold text-slate-900">
                  Gérer l'équipe
                </div>
                <div className="grid gap-3 max-w-sm">
                  <div>
                    <div className="mb-1 text-xs font-semibold text-slate-500">
                      Bio de l'équipe ({forms.teamBio.length}/300)
                    </div>
                    <textarea
                      value={forms.teamBio}
                      maxLength={300}
                      rows={3}
                      onChange={(e) => updateForm("teamBio", e.target.value)}
                      placeholder="Présentez votre équipe..."
                      className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pixy-blue"
                    />
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold text-slate-500">
                      Bannière de l'équipe (960 x 106 px, sera soumise à validation)
                    </div>
                    {myTeam.banner && (
                      <div className="mb-2 overflow-hidden rounded-2xl border border-slate-200">
                        <img
                          src={myTeam.banner}
                          alt="Bannière actuelle"
                          className="h-auto w-full"
                        />
                      </div>
                    )}
                    {myTeam.banner_status === "pending" && (
                      <p className="mb-2 text-xs font-semibold text-amber-600">
                        En attente de validation par un administrateur.
                      </p>
                    )}
                    {myTeam.banner_status === "rejected" && (
                      <p className="mb-2 text-xs font-semibold text-rose-600">
                        Bannière refusée. Vous pouvez en proposer une nouvelle.
                      </p>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => updateForm("teamBanner", e.target.files?.[0] || null)}
                      className="text-sm text-slate-600"
                    />
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold text-slate-500">
                      Avatar / logo de l'équipe
                    </div>
                    {myTeam.logo && (
                      <img
                        src={myTeam.logo}
                        alt="Avatar actuel"
                        className="mb-2 h-16 w-16 rounded-2xl border border-slate-200 object-cover"
                      />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => updateForm("teamEditLogo", e.target.files?.[0] || null)}
                      className="text-sm text-slate-600"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      runAction(() =>
                        updateTeam(myTeam.id, {
                          bio: forms.teamBio,
                          banner: forms.teamBanner,
                          logo: forms.teamEditLogo,
                        })
                      )
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-pixy-ink px-4 py-3 text-sm font-black text-white"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            )}

            {/* Invite members */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3 text-sm font-semibold text-slate-900">
                Inviter un joueur
              </div>
              <div className="grid gap-2 sm:grid-cols-[1fr,auto]">
                <select
                  value={forms.teamInviteUserId}
                  onChange={(e) => {
                    updateForm("teamInviteTeamId", String(myTeam.id));
                    updateForm("teamInviteUserId", e.target.value);
                  }}
                  className="h-10 rounded-2xl border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">Choisir un joueur</option>
                  {(social.suggested_users || []).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.username}
                    </option>
                  ))}
                </select>
                <FriendActionButton
                  tone="amber"
                  icon={Send}
                  onClick={() =>
                    runAction(() =>
                      inviteTeamMember({
                        teamId: myTeam.id,
                        userId: Number(forms.teamInviteUserId),
                      })
                    )
                  }
                >
                  Inviter
                </FriendActionButton>
              </div>
            </div>

            {/* Members list with kick */}
            {(myTeam.members?.length ?? 0) > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-2 text-sm font-semibold text-slate-900">
                  Membres ({myTeam.members.length})
                </div>
                <ul className="space-y-1.5">
                  {myTeam.members.map((member) => {
                    const isMe = member.user.id === profileIdentity.id;
                    const amOwnerOrCo = myTeam.members.some(
                      (m) => m.user.id === profileIdentity.id && (m.role === "chef" || m.role === "co_chef")
                    );
                    const canKick = amOwnerOrCo && !isMe && member.role !== "chef";
                    const roleLabel = member.role === "chef" ? "Chef" : member.role === "co_chef" ? "Co-chef" : "Membre";
                    return (
                      <li key={member.user.id} className="flex items-center justify-between gap-2 rounded-xl bg-white px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{member.user.username}</span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{roleLabel}</span>
                          {isMe && <span className="text-[10px] text-slate-400">(vous)</span>}
                        </div>
                        {canKick && (
                          <button
                            type="button"
                            onClick={() => runAction(() => kickTeamMember(myTeam.id, member.user.id))}
                            className="rounded-full border border-rose-200 px-2 py-0.5 text-[10px] font-bold text-rose-600 hover:bg-rose-50"
                          >
                            Expulser
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Delete team (leader only) */}
            {myTeam.members?.some((m) => m.user.id === profileIdentity.id && m.role === "chef") && (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
                <div className="text-xs font-semibold text-rose-700">
                  Zone dangereuse — supprimer l'équipe (irréversible)
                </div>
                {(myTeam.members?.length ?? 0) > 1 && (
                  <p className="mt-1 text-[11px] text-rose-500">Expulsez tous les membres avant de supprimer.</p>
                )}
                <button
                  type="button"
                  onClick={() => runAction(() => deleteTeam(myTeam.id))}
                  disabled={(myTeam.members?.length ?? 0) > 1}
                  className="mt-2 inline-flex items-center gap-2 rounded-full border border-rose-200 px-3 py-1.5 text-xs font-bold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Supprimer l'équipe
                </button>
              </div>
            )}
          </div>
        </ProfileSectionCard>
      ) : (
        <ProfileSectionCard
          title="Créer une équipe"
          subtitle="Rassemble tes amis dans une équipe. Minimum 100 parties jouées."
        >
          <div className="grid gap-3 max-w-sm">
            <input
              value={forms.teamName}
              maxLength={20}
              onChange={(e) => updateForm("teamName", e.target.value)}
              placeholder="Nom de l'équipe"
              className="h-11 rounded-2xl border border-slate-200 px-3 text-sm outline-none focus:border-pixy-blue"
            />
            <input
              value={forms.teamTag}
              maxLength={4}
              onChange={(e) => updateForm("teamTag", e.target.value.toUpperCase())}
              placeholder="Tag, ex: PIXY"
              className="h-11 rounded-2xl border border-slate-200 px-3 text-sm uppercase outline-none focus:border-pixy-blue"
            />
            <div>
              <div className="mb-1 text-xs font-semibold text-slate-500">Avatar (optionnel)</div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => updateForm("teamLogo", e.target.files?.[0] || null)}
                className="text-sm text-slate-600"
              />
            </div>
            <button
              type="button"
              onClick={() =>
                runAction(() =>
                  createTeam({
                    name: forms.teamName,
                    tag: forms.teamTag,
                    logo: forms.teamLogo,
                  })
                )
              }
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-pixy-ink px-4 py-3 text-sm font-black text-white"
            >
              <Users className="h-4 w-4" />
              Créer l'équipe
            </button>
          </div>
        </ProfileSectionCard>
      )}

      {/* Demandes et invitations équipe */}
      {teamRequests.length > 0 && (
        <ProfileSectionCard title="Demandes et invitations" subtitle="En attente de réponse">
          <div className="space-y-3">
            {teamRequests.map((item) => (
              <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="text-sm font-bold text-slate-900">
                  [{item.team.tag}] {item.team.name}
                </div>
                <div className="text-xs text-slate-500">
                  {item.request_type === "invitation"
                    ? `Invitation pour ${item.user.username}`
                    : `Demande de ${item.user.username}`}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.request_type === "request" && item.user.id !== profileIdentity.id ? (
                    <>
                      <FriendActionButton
                        tone="green"
                        icon={CheckCircle}
                        onClick={() => runAction(() => respondTeamRequest(item.id, "accept"))}
                      >
                        Accepter
                      </FriendActionButton>
                      <FriendActionButton
                        tone="light"
                        icon={X}
                        onClick={() => runAction(() => respondTeamRequest(item.id, "decline"))}
                      >
                        Refuser
                      </FriendActionButton>
                    </>
                  ) : item.request_type === "invitation" && item.user.id === profileIdentity.id ? (
                    <>
                      <FriendActionButton
                        tone="green"
                        icon={CheckCircle}
                        onClick={() => runAction(() => respondTeamRequest(item.id, "accept"))}
                      >
                        Accepter
                      </FriendActionButton>
                      <FriendActionButton
                        tone="light"
                        icon={X}
                        onClick={() => runAction(() => respondTeamRequest(item.id, "decline"))}
                      >
                        Refuser
                      </FriendActionButton>
                    </>
                  ) : (
                    <FriendActionButton
                      tone="light"
                      icon={X}
                      onClick={() => runAction(() => respondTeamRequest(item.id, "cancel"))}
                    >
                      Annuler
                    </FriendActionButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ProfileSectionCard>
      )}

      {/* Équipes publiques */}
      <ProfileSectionCard title="Équipes publiques" subtitle="Rejoindre une équipe existante">
        <div className="space-y-3">
          {publicTeams.map((team) => {
            const isMember = (team.members || []).some(
              (m) => String(m.user.id) === String(profileIdentity.id)
            );
            return (
              <div
                key={team.id}
                className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-3">
                  {team.logo ? (
                    <img src={team.logo} alt="" className="h-11 w-11 rounded-2xl object-cover" />
                  ) : (
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-xs font-black text-white">
                      {team.tag}
                    </div>
                  )}
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => navigate(`/equipe/t/${team.id}`)}
                      className="truncate text-left font-black text-slate-950 hover:underline"
                    >
                      [{team.tag}] {team.name}
                    </button>
                    <div className="text-xs text-slate-500">
                      {team.members.length} / 20 membre(s)
                    </div>
                  </div>
                </div>
                {isMember ? (
                  <FriendActionButton
                    tone="light"
                    icon={X}
                    onClick={() => runAction(() => leaveTeam(team.id))}
                  >
                    Quitter
                  </FriendActionButton>
                ) : myTeam ? (
                  <span className="text-xs text-slate-400">Déjà dans une équipe</span>
                ) : (
                  <FriendActionButton
                    tone="green"
                    icon={UserPlus}
                    onClick={() => runAction(() => requestJoinTeam(team.id))}
                  >
                    Demander
                  </FriendActionButton>
                )}
              </div>
            );
          })}
          {publicTeams.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
              Aucune équipe publique pour le moment.
            </div>
          )}
        </div>
      </ProfileSectionCard>
    </div>
  );
}
